/* ==========================================================================
   CUSTOM PATCH LIVE PREVIEW
   - Supports reusable Custom Product Field blocks
   - Supports legacy Custom Liquid inputs
   - Detects phone fields by target, ID, property name, or input type
   - Keeps the phone line hidden until a value is entered
   - Updates the preview while typing
   - Opens the preview slide when a connected input is focused
   - Automatically reduces font size when text is too long
   ========================================================================== */

(() => {
  const PREVIEW_SELECTOR = '[data-custom-patch-preview]';
  const FIELD_SELECTOR = '[data-custom-product-field]';
  const FIELD_INPUT_SELECTOR = '[data-custom-field-input]';

  const normaliseValue = (value, uppercase = false) => {
    let output = String(value || '')
      .replace(/\s+/g, ' ')
      .trim();

    if (uppercase) {
      output = output.toUpperCase();
    }

    return output;
  };

  const findInputByPreviewTarget = (productInfo, target) => {
    const normalisedTarget = String(target || '').toLowerCase();

    const fields = Array.from(
      productInfo.querySelectorAll(FIELD_SELECTOR)
    );

    const matchingField = fields.find((field) => {
      return String(field.dataset.previewTarget || '').toLowerCase() ===
        normalisedTarget;
    });

    if (!matchingField) return null;

    return (
      matchingField.querySelector(FIELD_INPUT_SELECTOR) ||
      matchingField.querySelector('input, textarea')
    );
  };

  const findPropertyInput = (productInfo, propertyNames) => {
    const inputs = Array.from(
      productInfo.querySelectorAll(
        'input[name^="properties["], textarea[name^="properties["]'
      )
    );

    return (
      inputs.find((input) => {
        const name = String(input.name || '').toLowerCase();

        return propertyNames.some((propertyName) => {
          return name === `properties[${propertyName.toLowerCase()}]`;
        });
      }) || null
    );
  };

  const findPrimaryInput = (productInfo) => {
    return (
      findInputByPreviewTarget(productInfo, 'primary') ||
      productInfo.querySelector('#dog-name') ||
      findPropertyInput(productInfo, [
        'DOG NAME',
        'Dog Name',
        'NAME',
        'Name',
      ])
    );
  };

  const findSecondaryInput = (productInfo) => {
    return (
      findInputByPreviewTarget(productInfo, 'secondary') ||
      productInfo.querySelector('#your-phone') ||
      findPropertyInput(productInfo, [
        'YOUR PHONE',
        'Your Phone',
        'PHONE NUMBER',
        'Phone Number',
        'PHONE',
        'Phone',
      ]) ||
      productInfo.querySelector(
        'input[type="tel"][name^="properties["]'
      ) ||
      productInfo.querySelector('input[type="tel"]')
    );
  };

  const fitLine = (line) => {
    if (!line || line.hidden) return;

    const container = line.parentElement;

    if (!container || container.clientWidth <= 0) return;

    line.style.removeProperty('font-size');

    const computedFontSize = Number.parseFloat(
      window.getComputedStyle(line).fontSize
    );

    if (!Number.isFinite(computedFontSize)) return;

    const minimumFontSize = line.matches(
      '.custom-patch-preview__line--secondary'
    )
      ? 9
      : 13;

    let minimum = minimumFontSize;
    let maximum = computedFontSize;
    let fittedSize = minimumFontSize;

    for (let index = 0; index < 9; index += 1) {
      const candidate = (minimum + maximum) / 2;

      line.style.fontSize = `${candidate}px`;

      if (line.scrollWidth <= container.clientWidth) {
        fittedSize = candidate;
        minimum = candidate;
      } else {
        maximum = candidate;
      }
    }

    line.style.fontSize = `${fittedSize}px`;
  };

  const fitPreview = (preview) => {
    window.requestAnimationFrame(() => {
      fitLine(
        preview.querySelector('[data-patch-preview-primary]')
      );

      fitLine(
        preview.querySelector('[data-patch-preview-secondary]')
      );
    });
  };

  const updatePreview = (preview) => {
    const productInfo = preview.closest('product-info');

    if (!productInfo) return;

    const primaryInput = findPrimaryInput(productInfo);
    const secondaryInput = findSecondaryInput(productInfo);

    const primaryLine = preview.querySelector(
      '[data-patch-preview-primary]'
    );

    const secondaryLine = preview.querySelector(
      '[data-patch-preview-secondary]'
    );

    const primaryDefault =
      preview.dataset.primaryDefault || 'YOUR TEXT';

    const primaryUppercase =
      preview.dataset.primaryUppercase !== 'false';

    const secondaryUppercase =
      preview.dataset.secondaryUppercase === 'true';

    if (primaryLine) {
      const primaryValue = normaliseValue(
        primaryInput ? primaryInput.value : '',
        primaryUppercase
      );

      primaryLine.textContent =
        primaryValue || primaryDefault;
    }

    if (secondaryLine) {
      const secondaryValue = normaliseValue(
        secondaryInput ? secondaryInput.value : '',
        secondaryUppercase
      );

      secondaryLine.textContent = secondaryValue;

      if (secondaryValue) {
        secondaryLine.removeAttribute('hidden');
      } else {
        secondaryLine.setAttribute('hidden', '');
        secondaryLine.style.removeProperty('font-size');
      }
    }

    fitPreview(preview);
  };

  const openPreviewSlide = (preview) => {
    if (preview.dataset.activateOnFocus !== 'true') return;

    const gallery = preview.closest('media-gallery');
    const slide = preview.closest('.pg-slide[data-media-id]');
    const mediaId = slide ? slide.dataset.mediaId : '';

    if (!gallery || !mediaId) return;

    if (typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(mediaId, true);
      return;
    }

    const matchingThumbnail = Array.from(
      gallery.querySelectorAll('.pg-thumb[data-target]')
    ).find((thumbnail) => {
      return thumbnail.dataset.target === mediaId;
    });

    const button = matchingThumbnail
      ? matchingThumbnail.querySelector('.pg-tbtn')
      : null;

    if (button) {
      button.click();
    }
  };

  const isConnectedInput = (productInfo, target) => {
    if (!(target instanceof HTMLInputElement) &&
        !(target instanceof HTMLTextAreaElement)) {
      return false;
    }

    const primaryInput = findPrimaryInput(productInfo);
    const secondaryInput = findSecondaryInput(productInfo);

    return target === primaryInput || target === secondaryInput;
  };

  const initialisePreview = (preview) => {
    if (!preview) return;

    if (preview.dataset.patchPreviewInitialised === 'true') {
      updatePreview(preview);
      return;
    }

    const productInfo = preview.closest('product-info');

    if (!productInfo) return;

    preview.dataset.patchPreviewInitialised = 'true';

    const handleInput = (event) => {
      if (!isConnectedInput(productInfo, event.target)) return;

      updatePreview(preview);
    };

    const handleChange = (event) => {
      if (!isConnectedInput(productInfo, event.target)) return;

      updatePreview(preview);
    };

    const handleFocus = (event) => {
      if (!isConnectedInput(productInfo, event.target)) return;

      openPreviewSlide(preview);
      updatePreview(preview);
    };

    const handleCustomFieldUpdate = () => {
      updatePreview(preview);
    };

    productInfo.addEventListener('input', handleInput);
    productInfo.addEventListener('change', handleChange);
    productInfo.addEventListener('focusin', handleFocus);

    productInfo.addEventListener(
      'custom-product-field:update',
      handleCustomFieldUpdate
    );

    preview._customPatchPreviewCleanup = () => {
      productInfo.removeEventListener('input', handleInput);
      productInfo.removeEventListener('change', handleChange);
      productInfo.removeEventListener('focusin', handleFocus);

      productInfo.removeEventListener(
        'custom-product-field:update',
        handleCustomFieldUpdate
      );
    };

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => {
        fitPreview(preview);
      });

      observer.observe(preview);
      preview._customPatchPreviewObserver = observer;
    }

    updatePreview(preview);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        updatePreview(preview);
      });
    }
  };

  const initialisePreviews = (scope = document) => {
    if (scope.matches && scope.matches(PREVIEW_SELECTOR)) {
      initialisePreview(scope);
    }

    if (!scope.querySelectorAll) return;

    scope
      .querySelectorAll(PREVIEW_SELECTOR)
      .forEach(initialisePreview);
  };

  const destroyPreview = (preview) => {
    if (!preview) return;

    if (preview._customPatchPreviewCleanup) {
      preview._customPatchPreviewCleanup();
    }

    if (preview._customPatchPreviewObserver) {
      preview._customPatchPreviewObserver.disconnect();
    }

    delete preview.dataset.patchPreviewInitialised;
  };

  const destroyPreviews = (scope) => {
    if (scope.matches && scope.matches(PREVIEW_SELECTOR)) {
      destroyPreview(scope);
    }

    if (!scope.querySelectorAll) return;

    scope
      .querySelectorAll(PREVIEW_SELECTOR)
      .forEach(destroyPreview);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialisePreviews(),
      { once: true }
    );
  } else {
    initialisePreviews();
  }

  document.addEventListener('product-info:loaded', (event) => {
    initialisePreviews(event.target);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialisePreviews(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    initialisePreviews(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyPreviews(event.target);
  });
})();